import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, Package, Search, User, AlertCircle, UserPlus, X as XIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateManualInvoice = ({ navigationParams, onClearParams, token: propToken }) => {
  const { token: authToken } = useAuth();
  const token = propToken || authToken;
  
  const [loading, setLoading] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [wrData, setWrData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ full_name: '', email: '', phone: '', locker_number: '' });
  const [creatingClient, setCreatingClient] = useState(false);
  
  // Search clients
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_email: '',
    client_address: '',
    client_id_document: '',
    customer_type: 'publico',
    invoice_number: '',
    payment_term_days: 15,
    due_date: '',
    comments: 'N/A',
    terms: 'Pago dentro de los términos especificados.',
    paid_amount: 0,
    warehouse_receipt_id: ''
  });

  const [items, setItems] = useState([
    { description: '', unit: 'ft3', quantity: '', rate: '', amount: 0 }
  ]);

  // Cargar datos del WR si vienen de navegación
  useEffect(() => {
    if (navigationParams?.wrData) {
      const wr = navigationParams.wrData;
      setWrData(wr);
      
      // Cargar datos del cliente
      if (wr.client_id) {
        loadClientData(wr.client_id);
      }
      
      // Prellenar datos del WR
      setFormData(prev => ({
        ...prev,
        warehouse_receipt_id: wr.id,
        comments: `WR: ${wr.wr_number || 'N/A'}`
      }));
      
      // Crear item de envío basado en el WR
      const shippingMethod = wr.shipping_method || 'Maritimo';
      const isAereo = shippingMethod.toLowerCase() === 'aereo';
      
      let quantity, unit, rate, description;
      
      if (isAereo) {
        // Para aéreo usar peso (lb)
        quantity = parseFloat(wr.final_weight_lb || wr.pmax || 0);
        unit = 'lb';
        rate = 6.5; // Tarifa default para aéreo
        description = `Envío Aéreo - WR ${wr.wr_number}`;
      } else {
        // Para marítimo usar volumen (ft³)
        quantity = parseFloat(wr.volume || wr.volume_ft3 || 0);
        unit = 'ft3';
        rate = 45; // Tarifa default para marítimo
        description = `Envío Marítimo - WR ${wr.wr_number}`;
      }
      
      const amount = (quantity * rate).toFixed(2);
      
      setItems([
        { 
          description, 
          unit, 
          quantity: quantity.toFixed(2), 
          rate: rate.toFixed(2), 
          amount 
        }
      ]);
    }
  }, [navigationParams]);

  const loadClientData = async (clientId) => {
    setLoadingClient(true);
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const client = response.data.find(c => c.id === clientId);
      if (client) {
        setClientData(client);
        setFormData(prev => ({
          ...prev,
          client_id: client.id,
          client_name: `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.full_name || client.email,
          client_email: client.email || '',
          client_address: client.delivery_address || client.address || '',
          client_id_document: client.cedula || client.national_id || '',
          customer_type: client.customer_type || 'publico'
        }));
      }
    } catch (error) {
    } finally {
      setLoadingClient(false);
    }
  };

  // Búsqueda de clientes
  const searchClients = async (term) => {
    if (!term || term.length < 2) {
      setClientSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = response.data.filter(c => 
        (c.email && c.email.toLowerCase().includes(term.toLowerCase())) ||
        (c.first_name && c.first_name.toLowerCase().includes(term.toLowerCase())) ||
        (c.last_name && c.last_name.toLowerCase().includes(term.toLowerCase())) ||
        (c.full_name && c.full_name.toLowerCase().includes(term.toLowerCase())) ||
        (c.locker_number && c.locker_number.toLowerCase().includes(term.toLowerCase()))
      ).slice(0, 10);
      
      setClientSearchResults(filtered);
    } catch (error) {
    }
  };

  const selectClient = (client) => {
    setClientData(client);
    setFormData(prev => ({
      ...prev,
      client_id: client.id,
      client_name: `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.full_name || client.email,
      client_email: client.email || '',
      client_address: client.delivery_address || client.address || '',
      client_id_document: client.cedula || client.national_id || '',
      customer_type: client.customer_type || 'publico'
    }));
    setShowClientSearch(false);
    setClientSearch('');
    setClientSearchResults([]);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddItem = () => {
    setItems([...items, { description: '', unit: 'ft3', quantity: '', rate: '', amount: 0 }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-calculate amount
    if (field === 'quantity' || field === 'rate') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = (qty * rate).toFixed(2);
    }
    
    setItems(newItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const invoiceData = {
        ...formData,
        items: items.filter(item => item.description.trim() !== '')
      };

      const response = await axios.post(`${API}/admin/invoices/manual`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ ${response.data.message}\nFactura: ${response.data.invoice_number}\nTotal: $${response.data.total}`);
      
      // Limpiar parámetros de navegación
      if (onClearParams) {
        onClearParams();
      }
      
      // Reset form
      setFormData({
        client_id: '',
        client_name: '',
        client_email: '',
        client_address: '',
        client_id_document: '',
        customer_type: 'publico',
        invoice_number: '',
        payment_term_days: 15,
        due_date: '',
        comments: 'N/A',
        terms: 'Pago dentro de los términos especificados.',
        paid_amount: 0,
        warehouse_receipt_id: ''
      });
      setItems([{ description: '', unit: 'ft3', quantity: '', rate: '', amount: 0 }]);
      setWrData(null);
      setClientData(null);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewClient = async () => {
    if (!newClientForm.full_name.trim() || !newClientForm.email.trim()) {
      alert('Nombre y email son obligatorios');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await axios.post(`${API}/admin/clients/quick-register`, newClientForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const created = res.data;
      // Auto-select the new client
      setFormData(prev => ({
        ...prev,
        client_id: created.id,
        client_name: created.full_name,
        client_email: created.email || '',
        client_address: created.address || '',
        client_id_document: created.id_document || ''
      }));
      setClientData(created);
      setShowNewClientModal(false);
      setNewClientForm({ full_name: '', email: '', phone: '', locker_number: '' });
    } catch (err) {
      alert('Error al crear cliente: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreatingClient(false);
    }
  };

  const hasValidClient = !!(formData.client_id && formData.client_name);

  return (
    <div className="max-w-5xl mx-auto p-4">
      {/* Banner si viene de un WR */}
      {wrData && (
        <div className="mb-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-800">
                📦 Generando factura desde Warehouse Receipt
              </p>
              <p className="text-sm text-blue-600">
                WR: <strong>{wrData.wr_number}</strong> | 
                Método: <strong>{wrData.shipping_method}</strong> | 
                {wrData.shipping_method?.toLowerCase() === 'aereo' 
                  ? ` Peso: ${wrData.final_weight_lb || wrData.pmax || 'N/A'} lb`
                  : ` Volumen: ${wrData.volume || wrData.volume_ft3 || 'N/A'} ft³`
                }
              </p>
              <p className="text-xs text-blue-500 mt-1">
                💡 Puedes agregar servicios adicionales (reempaque, caja, etc.) antes de crear la factura
              </p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            {wrData ? '📄 Crear Factura desde WR' : '📄 Crear Factura Manual'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sección de Cliente */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Datos del Cliente
                </h3>
                {!wrData && (
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowClientSearch(!showClientSearch)}
                    >
                      <Search className="w-4 h-4 mr-1" />
                      Buscar Cliente
                    </Button>
                    <Button 
                      type="button" 
                      variant="default" 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => setShowNewClientModal(true)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Nuevo Cliente
                    </Button>
                  </div>
                )}
              </div>

              {/* Búsqueda de cliente existente */}
              {showClientSearch && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                  <div className="relative">
                    <Input
                      placeholder="Buscar por nombre, email o casillero..."
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        searchClients(e.target.value);
                      }}
                    />
                    {clientSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                        {clientSearchResults.map(client => (
                          <div
                            key={client.id}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => selectClient(client)}
                          >
                            <p className="font-medium">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {client.email} | Casillero: {client.locker_number || 'N/A'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Cliente seleccionado */}
              {clientData && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">
                      ✓ Cliente seleccionado: {formData.client_name}
                    </p>
                    <p className="text-sm text-green-600">
                      {formData.client_email} | Casillero: {clientData.locker_number || 'N/A'}
                    </p>
                  </div>
                  {!wrData && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setClientData(null);
                        setFormData(prev => ({
                          ...prev,
                          client_id: '',
                          client_name: '',
                          client_email: '',
                          client_address: '',
                          client_id_document: '',
                          customer_type: 'publico'
                        }));
                      }}
                    >
                      Cambiar
                    </Button>
                  )}
                </div>
              )}

              {loadingClient ? (
                <div className="text-center py-4 text-gray-500">
                  Cargando datos del cliente...
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre Completo *</Label>
                    <Input 
                      name="client_name" 
                      value={formData.client_name} 
                      onChange={handleChange} 
                      required 
                      readOnly={!!clientData}
                      className={clientData ? 'bg-gray-50' : ''}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input 
                      name="client_email" 
                      type="email" 
                      value={formData.client_email} 
                      onChange={handleChange}
                      readOnly={!!clientData}
                      className={clientData ? 'bg-gray-50' : ''}
                    />
                  </div>
                  <div>
                    <Label>Cédula/Documento ID</Label>
                    <Input 
                      name="client_id_document" 
                      value={formData.client_id_document} 
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label>Tipo de Cliente</Label>
                    <select
                      name="customer_type"
                      value={formData.customer_type}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-md bg-white"
                      disabled={!!clientData}
                    >
                      <option value="publico">Público</option>
                      <option value="alumno">Alumno</option>
                      <option value="vip">VIP</option>
                    </select>
                  </div>
                  <div>
                    <Label>Dirección</Label>
                    <Input 
                      name="client_address" 
                      value={formData.client_address} 
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Datos de la Factura */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-3">Información de la Factura</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Número Factura (auto si vacío)</Label>
                  <Input name="invoice_number" value={formData.invoice_number} onChange={handleChange} placeholder="BILL17XXX" />
                </div>
                <div>
                  <Label>Plazo (días)</Label>
                  <Input name="payment_term_days" type="number" value={formData.payment_term_days} onChange={handleChange} />
                </div>
                <div>
                  <Label>Fecha Vencimiento</Label>
                  <Input name="due_date" type="date" value={formData.due_date} onChange={handleChange} />
                </div>
              </div>
            </div>

            {/* Items/Servicios */}
            <div className="border-b pb-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-lg">Servicios</h3>
                <Button type="button" size="sm" onClick={handleAddItem} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} className="mr-1" /> Agregar Servicio
                </Button>
              </div>
              
              {/* Nota informativa */}
              {wrData && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    El primer servicio se ha prellenado con los datos del WR. 
                    Puedes modificar las tarifas y agregar servicios adicionales como <strong>Reempaque</strong>, <strong>Caja</strong>, etc.
                  </p>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Descripción</th>
                      <th className="p-2 text-left w-24">Unidad</th>
                      <th className="p-2 text-left w-24">Cantidad</th>
                      <th className="p-2 text-left w-24">Tarifa</th>
                      <th className="p-2 text-left w-24">Monto</th>
                      <th className="p-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2">
                          <Input
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Envío Marítimo, Reempaque, etc."
                          />
                        </td>
                        <td className="p-2">
                          <select
                            className="w-full p-2 border rounded"
                            value={item.unit}
                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          >
                            <option value="ft3">ft³</option>
                            <option value="lb">lb</option>
                            <option value="Unit">Unit</option>
                          </select>
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                            placeholder="$"
                          />
                        </td>
                        <td className="p-2">
                          <Input value={`$${item.amount}`} readOnly className="bg-gray-50" />
                        </td>
                        <td className="p-2">
                          {items.length > 1 && (
                            <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveItem(index)}>
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Botones rápidos para agregar servicios comunes */}
              <div className="mt-3 flex gap-2 flex-wrap">
                <span className="text-sm text-gray-500">Agregar rápido:</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setItems([...items, { description: 'Reempaque', unit: 'Unit', quantity: '1', rate: '5.00', amount: '5.00' }])}
                >
                  + Reempaque ($5)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setItems([...items, { description: 'Caja', unit: 'Unit', quantity: '1', rate: '3.00', amount: '3.00' }])}
                >
                  + Caja ($3)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setItems([...items, { description: 'Embalaje especial', unit: 'Unit', quantity: '1', rate: '10.00', amount: '10.00' }])}
                >
                  + Embalaje ($10)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setItems([...items, { description: 'Seguro', unit: 'Unit', quantity: '1', rate: '0', amount: '0' }])}
                >
                  + Seguro
                </Button>
              </div>
              
              <div className="mt-4 flex justify-end">
                <div className="text-right space-y-2">
                  <div className="text-xl font-bold">
                    Total: ${calculateTotal()}
                  </div>
                  <div>
                    <Label>Monto Pagado</Label>
                    <Input 
                      name="paid_amount" 
                      type="number" 
                      step="0.01" 
                      value={formData.paid_amount} 
                      onChange={handleChange}
                      className="w-32"
                    />
                  </div>
                  <div className="text-lg text-red-600 font-semibold">
                    Saldo Pendiente: ${(calculateTotal() - formData.paid_amount).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Comentarios y Términos */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Comentarios</Label>
                <Textarea name="comments" value={formData.comments} onChange={handleChange} rows={3} />
              </div>
              <div>
                <Label>Términos y Condiciones</Label>
                <Textarea name="terms" value={formData.terms} onChange={handleChange} rows={3} />
              </div>
            </div>

            {!hasValidClient && !wrData && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <AlertCircle size={16} />
                Seleccione o cree un cliente para poder crear la factura
              </div>
            )}

            <Button 
              type="submit" 
              disabled={loading || (!hasValidClient && !wrData)} 
              className={`w-full ${hasValidClient || wrData ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
            >
              {loading ? 'Creando...' : wrData ? 'Crear Factura desde WR' : 'Crear Factura Manual'}
            </Button>
          </form>

          {/* Modal Nuevo Cliente */}
          {showNewClientModal && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNewClientModal(false)}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg">Registrar Nuevo Cliente</h3>
                  <button onClick={() => setShowNewClientModal(false)} className="text-gray-400 hover:text-gray-600">
                    <XIcon size={20} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input value={newClientForm.full_name} onChange={e => setNewClientForm(prev => ({...prev, full_name: e.target.value}))} placeholder="Juan Pérez" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={newClientForm.email} onChange={e => setNewClientForm(prev => ({...prev, email: e.target.value}))} placeholder="cliente@email.com" />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input value={newClientForm.phone} onChange={e => setNewClientForm(prev => ({...prev, phone: e.target.value}))} placeholder="+58 424-1234567" />
                  </div>
                  <div>
                    <Label>Código Casillero</Label>
                    <Input value={newClientForm.locker_number} onChange={e => setNewClientForm(prev => ({...prev, locker_number: e.target.value}))} placeholder="TDA000500" />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" className="flex-1" onClick={() => setShowNewClientModal(false)}>Cancelar</Button>
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleCreateNewClient} disabled={creatingClient}>
                    {creatingClient ? 'Creando...' : 'Crear Cliente'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateManualInvoice;
